// copied from apps/crm; consolidate into a shared package in S5 slice 4
// Live cases for this surface: the SSE stream de-duplicated by id, plus the
// local verdict state so a card settles the moment the person decides.
import { useCallback, useEffect, useRef, useState } from "react";
import type { Case, Verdict } from "@artemis-group/aiw-contracts";
import { caseState, type CaseUiState } from "@aiw/ui";
import { api, client } from "../lib/api";
import { subscribeCases } from "../lib/sse";

export interface LiveCase { kase: Case; state: CaseUiState; busy: boolean }

let shared: { cases: Map<string, LiveCase>; listeners: Set<() => void>; unsub: (() => void) | null; status: "open" | "closed" | "error" | "connecting" } | null = null;

export function resetCases(){const old=shared;shared=null;old?.unsub?.();old?.cases.clear();old?.listeners.clear();}

function store() {
  if (!shared) shared = { cases: new Map(), listeners: new Set(), unsub: null, status: "connecting" };
  return shared;
}
function notify() { for (const l of store().listeners) l(); }

function ensureStream() {
  const s = store();
  if (s.unsub) return;
  s.unsub = subscribeCases(
    (k) => { if(shared!==s)return;s.cases.set(k.id, { kase: k, state: caseState(k), busy: s.cases.get(k.id)?.busy ?? false }); notify(); },
    (st) => { if(shared!==s)return;s.status = st; notify(); },
  );
}

export function useCases() {
  const s = store();
  const [, tick] = useState(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    const l = () => { if (mounted.current) tick((n) => n + 1); };
    s.listeners.add(l);
    ensureStream();
    return () => { mounted.current = false; s.listeners.delete(l); };
  }, [s]);

  const decide = useCallback(async (id: string, decision: Verdict["decision"], extra?: { reason?: string | null }) => {
    if(shared!==s)return;
    const lc = s.cases.get(id);
    if (!lc) return;
    lc.busy = true; notify();
    try {
      // The server can record a rejection despite HTTP 403; always read back.
      try { await api.decide(client, { case_id: id, decision, reason: extra?.reason ?? null }); } catch { /* read the authoritative outcome below */ }
      if(shared!==s)return;
      const current = await api.case(client, id);
      if(shared!==s)return;
      s.cases.set(id, { kase: current, state: caseState(current), busy: false });
    } catch {
      lc.state = "failed";
    } finally {
      if(shared===s){lc.busy = false; notify();}
    }
  }, [s]);

  const list = [...s.cases.values()].sort((a, b) => a.kase.created_at.localeCompare(b.kase.created_at));
  return { cases: list, open: list.filter((c) => c.state === "proposed" || c.state === "revised"), status: s.status, decide };
}
